import { ReactNode } from 'react';
import { ArrowDown, ArrowRight, ArrowUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Sparkline, type SparklineTone } from './Sparkline';

/*
 * KpiTile — DESIGN_SYSTEM.md §2.4
 *
 * Dashboard KPI card. Slots:
 *   label (mono-eyebrow) — small uppercase descriptor
 *   value (kpi-num)       — big numeric / formatted value
 *   delta (chip)          — direction + value pill
 *   sparkline (optional)  — small trend chart
 *   foot (caption)        — supplementary text (period, context)
 *
 * The 4-col grid is owned by the consumer (`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4`).
 */

export type KpiDeltaDirection = 'up' | 'down' | 'flat';

export interface KpiDelta {
  direction: KpiDeltaDirection;
  /** Formatted delta value (e.g. "+12%", "−3"). */
  value: ReactNode;
  /** Override the inferred tone (default: up=success, down=danger, flat=muted). */
  tone?: SparklineTone;
}

interface KpiTileProps {
  label: ReactNode;
  value: ReactNode;
  delta?: KpiDelta;
  /** Trend series for the inline Sparkline. */
  series?: number[];
  /** Sparkline tone. Defaults to delta tone, then brand. */
  seriesTone?: SparklineTone;
  /** Footer caption (e.g. "vs last week"). */
  foot?: ReactNode;
  /** Optional leading icon, rendered in a 40px tinted square. */
  icon?: ReactNode;
  className?: string;
}

const DIRECTION_ICON: Record<KpiDeltaDirection, typeof ArrowUp> = {
  up:   ArrowUp,
  down: ArrowDown,
  flat: ArrowRight,
};

const DIRECTION_TONE: Record<KpiDeltaDirection, SparklineTone> = {
  up:   'success',
  down: 'danger',
  flat: 'muted',
};

const TONE_CHIP: Record<SparklineTone, string> = {
  brand:   'bg-brand-50 text-brand-700',
  success: 'bg-success-soft text-success-text',
  warn:    'bg-warn-soft text-warn-text',
  danger:  'bg-danger-soft text-danger-text',
  muted:   'bg-surface-2 text-soft',
};

const TONE_ICON_BG: Record<SparklineTone, string> = {
  brand:   'bg-brand-50 text-brand-500',
  success: 'bg-success-soft text-success',
  warn:    'bg-warn-soft text-warn',
  danger:  'bg-danger-soft text-danger',
  muted:   'bg-surface-2 text-soft',
};

export function KpiTile({
  label,
  value,
  delta,
  series,
  seriesTone,
  foot,
  icon,
  className,
}: KpiTileProps) {
  const deltaTone = delta?.tone ?? (delta ? DIRECTION_TONE[delta.direction] : undefined);
  const sparkTone: SparklineTone = seriesTone ?? deltaTone ?? 'brand';

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-2xl border border-line bg-surface p-5 shadow-card',
        'transition-shadow hover:brightness-[0.99]',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-mono-eyebrow text-soft">{label}</p>
          <p className="mt-2 text-kpi-num tabular-nums text-ink truncate">{value}</p>
        </div>
        {icon && (
          <div
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
              TONE_ICON_BG[sparkTone],
            )}
          >
            {icon}
          </div>
        )}
      </div>

      {(delta || series) && (
        <div className="flex items-center justify-between gap-3">
          {delta && (
            <KpiDeltaChip direction={delta.direction} value={delta.value} tone={delta.tone} />
          )}
          {series && series.length > 1 && (
            <Sparkline series={series} tone={sparkTone} fill className="ml-auto" />
          )}
        </div>
      )}

      {foot && (
        <p className="text-body-xs text-soft">{foot}</p>
      )}
    </div>
  );
}

interface KpiDeltaChipProps {
  direction: KpiDeltaDirection;
  value: ReactNode;
  tone?: SparklineTone;
}

function KpiDeltaChip({ direction, value, tone }: KpiDeltaChipProps) {
  const Icon = DIRECTION_ICON[direction];
  const resolvedTone = tone ?? DIRECTION_TONE[direction];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-pill px-2 py-0.5 text-body-xs font-semibold tabular-nums',
        TONE_CHIP[resolvedTone],
      )}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {value}
    </span>
  );
}
